import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp, Search, Phone } from 'lucide-react';
import { fetchCollection } from '../../lib/cms';
import SEO from '../../components/SEO';

export default function FAQDe() {
  const [faqList, setFaqList] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadData() {
      const data = await fetchCollection('faq', 'de');
      setFaqList(data);
    }
    loadData();
  }, []);

  const toggleFaq = (index) => {
    if (openIndex === index) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  const filteredFaq = faqList.filter(
    (item) =>
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer ? item.answer.replace(/<[^>]*>?/gm, '') : '',
      },
    })),
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Häufig gestellte Fragen (FAQ) - Danshaker"
        description="Finden Sie Antworten auf häufig gestellte Fragen zum Danshaker: Akkulaufzeit, Einstreuarten, Amortisation, Miete und Reinigung."
        path="/de/faq"
        schema={faqSchema}
        lang="de"
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Header Section */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-12 md:px-8 md:pt-24">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
            <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Häufig gestellte Fragen</span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            FAQ
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Hier finden Sie klare Antworten auf die häufigsten Fragen rund um Funktion, Einstreueignung, Akku und Miete des Danshakers.
          </p>

          {/* Search Bar */}
          <div className="pt-4 max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="In den Fragen suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white py-3.5 pl-12 pr-4 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-[#0E67A2] focus:outline-none focus:ring-2 focus:ring-[#0E67A2]/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion List */}
      <section className="mx-auto max-w-4xl px-4 py-8 md:px-8 pb-20">
        <div className="space-y-4">
          {filteredFaq.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-[#0E67A2]/30"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(idx)}
                  className="flex w-full items-center justify-between p-6 text-left font-bold text-slate-900 transition hover:text-[#0E67A2]"
                  aria-expanded={isOpen}
                >
                  <span className="text-base sm:text-lg pr-4">{item.question}</span>
                  {isOpen ? (
                    <ChevronUp className="h-5 w-5 shrink-0 text-[#0E67A2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 shrink-0 text-slate-400" />
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 px-6 pt-4 pb-6">
                    <div
                      className="text-slate-600 leading-relaxed text-sm sm:text-base font-medium prose prose-slate max-w-none"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {filteredFaq.length === 0 && (
            <div className="text-center py-12 rounded-2xl border border-dashed border-slate-300 p-8">
              <p className="text-slate-500 font-medium">Keine passenden Fragen für "{searchQuery}" gefunden.</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 text-sm font-bold text-[#0E67A2] hover:underline"
              >
                Suche zurücksetzen
              </button>
            </div>
          )}
        </div>

        {/* Contact Teaser */}
        <div className="mt-16 rounded-3xl bg-slate-900 p-8 text-white text-center shadow-xl space-y-4">
          <h3 className="text-2xl font-bold">Haben Sie weitere Fragen?</h3>
          <p className="text-slate-300 text-sm max-w-md mx-auto">
            Wir beraten Sie gerne unverbindlich. Rufen Sie uns an oder senden Sie uns eine Nachricht.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-4">
            <a
              href="tel:25303589"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 font-bold text-slate-900 transition hover:bg-slate-100"
            >
              <Phone className="h-4 w-4 text-[#0E67A2]" />
              +45 2530 3589
            </a>
            <Link
              to="/de/kontakt"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3 font-bold text-white transition hover:bg-[#0E67A2]/90"
            >
              Kontaktformular
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
